import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import logoColor from 'assets/logoColor.png'
import { PATHS } from 'utils/paths'

const PrivacyPolicy = () => {
  const navigate = useNavigate()
  const navigateToLogin = () => {
    return navigate(PATHS.login)
  }

  return (
    <Wrapper>
      <LogoWrapper onClick={navigateToLogin}>
        <Logo src={logoColor} alt='logo' />
      </LogoWrapper>
      <Title>Privacy Policy</Title>
      <Subtitle>
        Contact Information of Digital Shelf Sp. z o.o., Data Controller:
      </Subtitle>
      <Paragraph>
        Digital Shelf Sp. z o.o., ul. Aleksandra Gieysztora 2/52, 02-999 Warsaw
      </Paragraph>
      <Subtitle>Data Protection Legislation</Subtitle>
      <Paragraph>
        This Privacy Policy refers to the following data protection regulations:{' '}
        <SubParagraph>
          Regulation (EU) 2016/679 of the European Parliament and of the Council
          of 27 April 2016 on the protection of natural persons with regard to
          the processing of personal data and on the free movement of such data,
          and repealing Directive 95/46/EC (General Data Protection Regulation)
          (OJ L 119, p. 1) – also known as “GDPR” (applied from 25.05.2018 along
          with new national regulations).
        </SubParagraph>
      </Paragraph>
      <Subtitle>Principles of Personal Data Processing</Subtitle>
      <Paragraph>
        Below are the key principles we follow:
        <BulletPoint>
          We collect personal data only to the minimal extent necessary to
          achieve the purposes for which they are collected.
        </BulletPoint>
        <BulletPoint>
          The purposes for collecting Users' personal data are clearly defined
          and based on legal provisions – we do not process data in a manner
          inconsistent with these purposes. We process data for the following
          purposes:
        </BulletPoint>
        <SubBulletPoint>
          Execution of contracts (GDPR Art. 6(1)(b));
        </SubBulletPoint>
        <SubBulletPoint>
          Fulfillment of legal obligations of the Controller (GDPR Art.
          6(1)(c));
        </SubBulletPoint>
        <SubBulletPoint>
          Based on previously given consent to the extent and purpose specified
          in the consent (GDPR Art. 6(1)(a));
        </SubBulletPoint>
        <SubBulletPoint>
          For purposes arising from legitimate interests pursued by the
          controller (GDPR Art. 6(1)(f)).
        </SubBulletPoint>
        <BulletPoint>
          We ensure the accuracy and correctness of Users' personal data and
          promptly (within 30 days at the latest) respond to any requests for
          rectification or updating of data. This period may be extended due to
          the complex nature of the request or the number of requests by an
          additional 60 days, of which we will inform.
        </BulletPoint>
        <BulletPoint>
          We realize the right of Users to access their personal data and to
          correct them.
        </BulletPoint>
        <BulletPoint>
          We also realize the rights of Users to delete personal data, withdraw
          consent, restrict processing, data portability, object to data
          processing, and not be subject to a decision based solely on automated
          processing, including profiling.
        </BulletPoint>
        <BulletPoint>
          We limit the storage of personal data in accordance with legal
          provisions, only to the period necessary to achieve the purposes for
          which they are collected, unless there are events that may extend the
          data storage period.
        </BulletPoint>
        <BulletPoint>
          We protect Users' personal data from loss, unauthorized access,
          accidental loss or alteration, and other unlawful forms of processing.
        </BulletPoint>
        <BulletPoint>
          If personal data are shared with other entities, it is done securely,
          contractually protected, and in accordance with applicable laws.
        </BulletPoint>
        <BulletPoint>
          Data will be processed for the period during which the user account is
          registered in the system, the period resulting from our legal
          obligations, or if processed based on consent – until its withdrawal.
        </BulletPoint>
        <BulletPoint>
          Personal data may be transferred to processing entities with whom we
          cooperate to provide our services, e.g., companies providing IT
          systems, companies providing services necessary for tasks related to
          the functioning of the system, applications.
        </BulletPoint>
      </Paragraph>
      <Subtitle>Users' Rights</Subtitle>
      <Paragraph>
        The User has the right to:
        <BulletPoint>
          Withdraw consent to the processing of personal data at any time.
        </BulletPoint>
        <BulletPoint>
          Access data: the right to receive confirmation whether personal data
          are being processed and how.
        </BulletPoint>
        <BulletPoint>
          Correct outdated or inaccurate personal data, and the right to
          complete them if they are incomplete.
        </BulletPoint>
        <BulletPoint>
          Object to the processing of personal data if we process personal data
          based on our legitimate interest (e.g., analytical, evidential,
          archival purposes); in case of an objection, we must stop processing
          data unless we demonstrate compelling legitimate grounds for
          processing that should objectively take precedence over the
          inconvenience or involve defending our claims (e.g., in legal
          proceedings). The right to object also applies if the processing
          involves direct marketing, including profiling, based on our
          legitimate interest, e.g., in connection with profiling based on
          cookies. In this case, processing of personal data for this purpose
          must be immediately stopped.
        </BulletPoint>
        <BulletPoint>
          Delete personal data (“right to be forgotten”) generally means
          requesting the controller to immediately delete the relevant personal
          data.
        </BulletPoint>
        <BulletPoint>
          Restrict the processing of personal data, which in practical terms may
          involve temporarily blocking user access to data or transferring data
          to another system.
        </BulletPoint>
        <BulletPoint>
          Data portability: the right to receive a copy of personal data if the
          processing is based on consent or on a contract and is carried out in
          an automated manner.
        </BulletPoint>
        <BulletPoint>
          Lodge a complaint with the supervisory authority – the President of
          the Personal Data Protection Office.
        </BulletPoint>
        <BulletPoint>
          Any requests regarding the processed personal data, including the
          rights entitled, can be sent by email to{' '}
          <MailTo href='mailto:office@digitalshelf.pl'>
            office@digitalshelf.pl
          </MailTo>{' '}
          or by mail to Digital Shelf Sp. z o.o., ul. Aleksandra Gieysztora
          2/52, 02-999 Warsaw.
        </BulletPoint>
        <BulletPoint>
          Requests will be fulfilled without undue delay, but no later than
          within 30 days after receiving it. This period may be extended due to
          the complex nature of the request or the number of requests by an
          additional 60 days, of which we will inform. Until the request is
          fulfilled, it may happen that information from which consent was
          withdrawn will be sent.
        </BulletPoint>
      </Paragraph>
      <Subtitle>Consent – Withdrawal of Consent</Subtitle>
      <Paragraph>
        In relation with the User, also within the services offered through the
        application, the User may be asked to consent to the processing of
        personal data for marketing purposes, including electronic marketing, if
        consent constitutes the proper legal basis for the processing of
        personal data. We do not require consent when it is not necessary in a
        specific case (although the obligation to obtain consent may result from
        other legal provisions, e.g., consent for telephone or electronic
        marketing).
        <SubParagraph>
          The User has the right to withdraw consent by contacting the
          Controller.
        </SubParagraph>
        Withdrawal of consent does not involve any negative consequences or
        inconveniences – however, you must know that withdrawal may result in
        the inability to use services for which the law requires us to obtain
        consent.
        <SubParagraph>
          Withdrawal of consent does not affect the lawfulness of the processing
          carried out on the basis of consent before its withdrawal.
        </SubParagraph>
      </Paragraph>
      <Subtitle>Data Storage</Subtitle>
      <Paragraph>
        The Controller stores personal data for the period necessary to achieve
        the purposes for which the User has been informed. The period of storing
        personal data is strictly in accordance with applicable legal
        provisions. The User has the right to obtain information from us about
        the probable period of storing personal data.
      </Paragraph>
      <Subtitle>Sharing Data with Other Entities</Subtitle>
      <Paragraph>
        With the User's consent, depending on the purpose of processing data and
        the content of the consent to processing and sharing, personal data may
        be shared with other entities.
      </Paragraph>
      <Subtitle>Profiling</Subtitle>
      <Paragraph>
        As part of processing Users' personal data, we may carry out profiling.
      </Paragraph>
      <Subtitle>Changes to the Policy</Subtitle>
      <Paragraph>
        The Controller reserves the right to make changes to this policy, which
        may result from the need to adapt to changes in the law or applicable
        privacy standards, or to expand our offer.
      </Paragraph>
      <Subtitle>Admin contact:</Subtitle>
      <Paragraph>
        <Paragraph>
          <MailTo href='mailto:office@digitalshelf.pl'>
            office@digitalshelf.pl
          </MailTo>
          <SubParagraph>
            Digital Shelf Sp. z o.o, ul. Aleksandra Gieysztora 2/52, 02-999
            Warszawa.
          </SubParagraph>
        </Paragraph>
      </Paragraph>
      <Copyright>
        <span>&#169; SINDYCAT</span>
      </Copyright>      
      <BottomLine />
    </Wrapper>
  )
}

export default PrivacyPolicy

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: center;
  padding: 120px 240px;
  position: relative;
`
const Title = styled.h1`
  font-size: 2rem;
  text-align: center;
  font-weight: bold;
`
const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  padding-bottom: 12px;
`
const Paragraph = styled.p`
  font-size: 1rem;
  padding-bottom: 36px;
`
const SubParagraph = styled.p`
  font-size: 1rem;
  padding: 24px 0;
`
const BulletPoint = styled.p`
  display: list-item;
  font-size: 1rem;
  margin-left: 36px;
`
const SubBulletPoint = styled.p`
  display: list-item;
  font-size: 1rem;
  margin-left: 52px;
`
const MailTo = styled.a`
  color: blue;
`
const Copyright = styled.div`
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  text-align: center;
`
const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 32px;
  left: 0;
  right: 0;
  width: 100%;
`
const Logo = styled.img`
  height: ${({ theme }) => theme.dimensions.logoColorHeight + 'px'};
`
const BottomLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 24px;
  background: ${({ theme }) => theme.gradients.bottomLine};
`
